import React from 'react';
import { School, Users, GraduationCap, Heart, BookOpen, ShieldCheck, Activity, TrendingUp, AlertTriangle, CheckCircle2, Clock, Server, RefreshCw, Tag } from 'lucide-react';
import { motion } from 'framer-motion';
import { PageLayout } from '../../shared/PageLayout';
import { StatCard } from '../../shared/StatCard';
import { ConnectionBadge } from '../shared/ConnectionBadge';
import { QualityRing, ProgressBar, containerVariants, itemVariants } from './dashboard/DashboardCharts';
import { useDashboardData } from './dashboard/useDashboardData';
export function DashboardPage({
  onNavigate
}: {
  onNavigate?: (view: string) => void;
}) {
  const {
    stats,
    contentStats,
    pipelineLoading,
    strictModeOn,
    handleRefreshPipeline,
    passCount,
    warnCount,
    failCount,
    avgScore,
    totalValidated,
    systemHealth,
    recentActivity
  } = useDashboardData();
  return <PageLayout title="Dashboard" subtitle="Overzicht van het Meester djay platform" actions={<ConnectionBadge status="live" data-id="element-4822" />} data-id="element-4821">
      {/* Section 1: Platform Overview */}
      <motion.div variants={containerVariants} initial="hidden" animate="show" className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 mb-6" data-id="element-4823">
        <motion.div variants={itemVariants} data-id="element-4824">
          <StatCard label="Leerlingen" value={stats.totalLearners} icon={<Users className="w-4 h-4 text-blue-500" data-id="element-4826" />} data-id="element-4825" />
        </motion.div>
        <motion.div variants={itemVariants} data-id="element-4827">
          <StatCard label="Docenten" value={stats.totalTeachers} icon={<GraduationCap className="w-4 h-4 text-indigo-500" data-id="element-4829" />} data-id="element-4828" />
        </motion.div>
        <motion.div variants={itemVariants} data-id="element-4830">
          <StatCard label="Ouders" value={stats.totalParents} icon={<Heart className="w-4 h-4 text-rose-500" data-id="element-4832" />} data-id="element-4831" />
        </motion.div>
        <motion.div variants={itemVariants} data-id="element-4833">
          <StatCard label="Scholen" value={stats.totalSchools} icon={<School className="w-4 h-4 text-purple-500" data-id="element-4835" />} data-id="element-4834" />
        </motion.div>
      </motion.div>

      {/* Section 2: Content & Quality */}
      <motion.div variants={containerVariants} initial="hidden" animate="show" className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 sm:p-5 mb-6" data-id="element-4836">
        <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4" data-id="element-4837">
          Content & Kwaliteit
        </h3>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-4838">
          {/* Left: Content Stats */}
          <div className="space-y-4" data-id="element-4839">
            <div className="flex items-center justify-between" data-id="element-4840">
              <div className="flex items-center gap-2" data-id="element-4841">
                <BookOpen className="w-4 h-4 text-blue-500" data-id="element-4842" />
                <span className="text-sm font-medium text-gray-700" data-id="element-4843">
                  Vragenbank
                </span>
              </div>
              <span className="text-lg font-bold text-gray-900" data-id="element-4844">
                {contentStats.totalQuestions.toLocaleString('nl')}
              </span>
            </div>
            <div className="flex items-center justify-between" data-id="element-4845">
              <div className="flex items-center gap-2" data-id="element-4846">
                <Tag className="w-4 h-4 text-purple-500" data-id="element-4847" />
                <span className="text-sm font-medium text-gray-700" data-id="element-4848">
                  Families
                </span>
              </div>
              <span className="text-sm text-gray-600" data-id="element-4849">
                {contentStats.totalFamilies} / {contentStats.taxonomyFamilies}
              </span>
            </div>
            <ProgressBar value={contentStats.coveragePct} color={contentStats.coveragePct >= 80 ? 'bg-emerald-500' : contentStats.coveragePct >= 50 ? 'bg-amber-500' : 'bg-red-500'} label="Seed dekking" data-id="element-4850" />
            <ProgressBar value={contentStats.visualCoveragePct} color={contentStats.visualCoveragePct >= 80 ? 'bg-emerald-500' : contentStats.visualCoveragePct >= 50 ? 'bg-amber-500' : 'bg-red-500'} label={`Visuele componenten (${contentStats.usedVisualsCount}/${contentStats.registeredVisuals})`} data-id="element-4851" />
          </div>

          {/* Right: Quality & Validation */}
          <div className="space-y-4" data-id="element-4852">
            <div className="flex items-center justify-between" data-id="element-4853">
              <div className="flex items-center gap-3" data-id="element-4854">
                {avgScore !== null ? <QualityRing score={avgScore} data-id="element-4855" /> : <div className="w-12 h-12 rounded-full bg-gray-100 animate-pulse" data-id="element-4856" />}
                <div data-id="element-4857">
                  <div className="text-sm font-medium text-gray-700" data-id="element-4858">
                    Kwaliteitsscore
                  </div>
                  <div className="text-xs text-gray-500" data-id="element-4859">
                    alle {contentStats.totalQuestions.toLocaleString('nl')}{' '}
                    vragen
                  </div>
                </div>
              </div>
              <button onClick={handleRefreshPipeline} disabled={pipelineLoading} className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-40" title="Validatie vernieuwen" data-id="element-4860">
                <RefreshCw className={`w-4 h-4 ${pipelineLoading ? 'animate-spin' : ''}`} data-id="element-4861" />
              </button>
            </div>

            {pipelineLoading ? <div className="flex gap-4" data-id="element-4862">
                {['Geslaagd', 'Warnings', 'Fouten'].map(l => <div key={l} className="flex-1 bg-gray-50 rounded-lg p-2 animate-pulse" data-id="element-4863">
                    <div className="h-5 bg-gray-200 rounded mb-1" data-id="element-4864" />
                    <div className="h-3 bg-gray-100 rounded w-2/3" data-id="element-4865" />
                  </div>)}
              </div> : passCount !== null ? <>
                <div className="flex gap-3" data-id="element-4866">
                  <div className="flex-1 bg-emerald-50 rounded-lg p-2.5 text-center" data-id="element-4867">
                    <div className="text-lg font-bold text-emerald-700" data-id="element-4868">
                      {passCount}
                    </div>
                    <div className="text-xs text-emerald-600" data-id="element-4869">Geslaagd</div>
                  </div>
                  <div className="flex-1 bg-amber-50 rounded-lg p-2.5 text-center" data-id="element-4870">
                    <div className="text-lg font-bold text-amber-700" data-id="element-4871">
                      {warnCount}
                    </div>
                    <div className="text-xs text-amber-600" data-id="element-4872">Warnings</div>
                  </div>
                  <div className="flex-1 bg-red-50 rounded-lg p-2.5 text-center" data-id="element-4873">
                    <div className="text-lg font-bold text-red-700" data-id="element-4874">
                      {failCount}
                    </div>
                    <div className="text-xs text-red-600" data-id="element-4875">Fouten</div>
                  </div>
                </div>
                <div className="h-2 bg-gray-100 rounded-full overflow-hidden flex" data-id="element-4876">
                  <div className="h-full bg-emerald-500" style={{
                width: `${totalValidated > 0 ? (passCount ?? 0) / totalValidated * 100 : 0}%`
              }} data-id="element-4877" />
                  <div className="h-full bg-amber-400" style={{
                width: `${totalValidated > 0 ? (warnCount ?? 0) / totalValidated * 100 : 0}%`
              }} data-id="element-4878" />
                  <div className="h-full bg-red-500" style={{
                width: `${totalValidated > 0 ? (failCount ?? 0) / totalValidated * 100 : 0}%`
              }} data-id="element-4879" />
                </div>
              </> : <div className="text-sm text-gray-400 text-center py-4" data-id="element-4880">
                Validatie wordt geladen...
              </div>}
          </div>
        </div>
      </motion.div>

      {/* Section 3: Activity & System Health */}
      <motion.div variants={containerVariants} initial="hidden" animate="show" className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 mb-6" data-id="element-4881">
        {/* Recent Activity */}
        <motion.div variants={itemVariants} className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 sm:p-5" data-id="element-4882">
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4 flex items-center gap-2" data-id="element-4883">
            <Clock className="w-4 h-4" data-id="element-4884" /> Recente Activiteit
          </h3>
          <div className="space-y-3" data-id="element-4885">
            {recentActivity.map((item, i) => <div key={i} className="flex items-start gap-3" data-id="element-4886">
                <div className={`mt-0.5 flex-shrink-0 ${item.type === 'success' ? 'text-emerald-500' : item.type === 'warning' ? 'text-amber-500' : 'text-blue-500'}`} data-id="element-4887">
                  {item.type === 'success' ? <CheckCircle2 className="w-4 h-4" data-id="element-4888" /> : item.type === 'warning' ? <AlertTriangle className="w-4 h-4" data-id="element-4889" /> : <Activity className="w-4 h-4" data-id="element-4890" />}
                </div>
                <div className="min-w-0 flex-1" data-id="element-4891">
                  <p className="text-sm text-gray-700 leading-snug" data-id="element-4892">
                    {item.message}
                  </p>
                  <p className="text-xs text-gray-400 mt-0.5 font-mono" data-id="element-4893">
                    {item.time}
                  </p>
                </div>
              </div>)}
          </div>
        </motion.div>

        {/* System Health */}
        <motion.div variants={itemVariants} className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 sm:p-5" data-id="element-4894">
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4 flex items-center gap-2" data-id="element-4895">
            <Server className="w-4 h-4" data-id="element-4896" /> Systeemgezondheid
          </h3>
          <div className="space-y-4" data-id="element-4897">
            <div className="flex items-center justify-between" data-id="element-4898">
              <span className="text-sm text-gray-600" data-id="element-4899">Strict Mode</span>
              <span className={`text-xs font-bold px-2 py-1 rounded-full ${strictModeOn ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-500'}`} data-id="element-4900">
                {strictModeOn ? 'AAN' : 'UIT'}
              </span>
            </div>
            <div className="flex items-center justify-between" data-id="element-4901">
              <span className="text-sm text-gray-600" data-id="element-4902">Gem. responstijd</span>
              <span className="text-sm font-semibold text-gray-800" data-id="element-4903">
                {systemHealth.responseTime}ms
              </span>
            </div>
            <div data-id="element-4904">
              <div className="flex items-center justify-between mb-1.5" data-id="element-4905">
                <span className="text-sm text-gray-600" data-id="element-4906">Opslag</span>
                <span className="text-sm font-semibold text-gray-800" data-id="element-4907">
                  {systemHealth.storageMb} / 5 MB
                </span>
              </div>
              <div className="h-2 bg-gray-100 rounded-full overflow-hidden" data-id="element-4908">
                <div className="h-full bg-violet-500 rounded-full" style={{
                width: `${systemHealth.storagePct}%`
              }} data-id="element-4909" />
              </div>
            </div>
            <div data-id="element-4910">
              <div className="flex items-center justify-between mb-1.5" data-id="element-4911">
                <span className="text-sm text-gray-600" data-id="element-4912">Actief deze week</span>
                <span className="text-sm font-semibold text-gray-800" data-id="element-4913">
                  {stats.activeThisWeek} leerlingen
                </span>
              </div>
              <div className="h-2 bg-gray-100 rounded-full overflow-hidden" data-id="element-4914">
                <div className="h-full bg-blue-500 rounded-full" style={{
                width: `${Math.min(100, stats.activeThisWeek / Math.max(stats.totalLearners, 1) * 100)}%`
              }} data-id="element-4915" />
              </div>
            </div>
          </div>
        </motion.div>
      </motion.div>

      {/* Section 4: Quick Insights */}
      <motion.div variants={containerVariants} initial="hidden" animate="show" className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4" data-id="element-4916">
        {/* Subscriptions */}
        <motion.div variants={itemVariants} className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 sm:p-5" data-id="element-4917">
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4" data-id="element-4918">
            Abonnementen
          </h3>
          <div className="space-y-2.5" data-id="element-4919">
            {stats.planDistribution.map(item => <div key={item.label} data-id="element-4920">
                <div className="flex justify-between text-xs mb-1" data-id="element-4921">
                  <span className="text-gray-600" data-id="element-4922">{item.label}</span>
                  <span className="font-semibold text-gray-800" data-id="element-4923">
                    {item.count}
                  </span>
                </div>
                <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden" data-id="element-4924">
                  <div className={`h-full rounded-full ${item.color}`} style={{
                width: `${item.pct}%`
              }} data-id="element-4925" />
                </div>
              </div>)}
          </div>
        </motion.div>

        {/* Top Learners */}
        <motion.div variants={itemVariants} className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 sm:p-5" data-id="element-4926">
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4 flex items-center gap-1.5" data-id="element-4927">
            <TrendingUp className="w-3.5 h-3.5" data-id="element-4928" /> Top Leerlingen
          </h3>
          {stats.topLearners.length > 0 ? <div className="space-y-2.5" data-id="element-4929">
              {stats.topLearners.map((item, i) => <div key={i} className="flex items-center gap-2.5" data-id="element-4930">
                  <div className="w-6 h-6 rounded-full bg-violet-100 text-violet-700 text-xs font-bold flex items-center justify-center flex-shrink-0" data-id="element-4931">
                    {i + 1}
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-4932">
                    <p className="text-xs font-medium text-gray-800 truncate" data-id="element-4933">
                      {item.name}
                    </p>
                  </div>
                  <span className="text-xs font-bold text-violet-600" data-id="element-4934">
                    {item.xp.toLocaleString('nl-NL')} XP
                  </span>
                </div>)}
            </div> : <p className="text-xs text-gray-400" data-id="element-4935">Geen leerlingen gevonden</p>}
        </motion.div>

        {/* Calibration Progress */}
        <motion.div variants={itemVariants} className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 sm:p-5" data-id="element-4936">
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4 flex items-center gap-1.5" data-id="element-4937">
            <ShieldCheck className="w-3.5 h-3.5" data-id="element-4938" /> Kalibratie
          </h3>
          <div className="space-y-3" data-id="element-4939">
            <div className="flex items-end justify-between" data-id="element-4940">
              <span className="text-2xl font-bold text-gray-900" data-id="element-4941">
                {contentStats.calibration.goldStandardPct}%
              </span>
              <span className="text-xs text-gray-500" data-id="element-4942">
                {contentStats.calibration.familiesAtGoldStandard} /{' '}
                {contentStats.calibration.totalFamilies} goud
              </span>
            </div>
            <div className="h-2 bg-gray-100 rounded-full overflow-hidden" data-id="element-4943">
              <motion.div initial={{
              width: 0
            }} animate={{
              width: `${contentStats.calibration.goldStandardPct}%`
            }} transition={{
              duration: 1,
              delay: 0.3
            }} className={`h-full rounded-full ${contentStats.calibration.goldStandardPct >= 80 ? 'bg-emerald-500' : contentStats.calibration.goldStandardPct >= 40 ? 'bg-amber-500' : 'bg-red-500'}`} data-id="element-4944" />
            </div>
            <div className="grid grid-cols-2 gap-2 text-xs" data-id="element-4945">
              <div className="flex justify-between" data-id="element-4946">
                <span className="text-gray-500" data-id="element-4947">citoDifficulty</span>
                <span className="font-semibold text-gray-700" data-id="element-4948">
                  {contentStats.calibration.citoDifficultyCoveragePct}%
                </span>
              </div>
              <div className="flex justify-between" data-id="element-4949">
                <span className="text-gray-500" data-id="element-4950">solutionSteps</span>
                <span className="font-semibold text-gray-700" data-id="element-4951">
                  {contentStats.calibration.solutionStepsCoveragePct}%
                </span>
              </div>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </PageLayout>;
}